import AddressButton from "@components/button/AddressButton";
import BasicButton from "@components/button/BasicButton";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import useUserInfoStore from "@store/useUserInfoStore";
import { useApiRequest } from "@utils/ApiRequest";
import { useMutation, useQuery } from "@tanstack/react-query";
import TossPaymentButton from "@components/button/TossPaymentButton";
import BasicModal from "@components/modal/BasicModal";
import { ModalContent } from "@components/modal/ModalContent";
import { queryClient } from "@libs/reactQuery/queryClient";
import { useTheme } from "@emotion/react";
import useOrderStore, {
  CartData,
  PurchaseData,
} from "@store/order/useOrderStore";
import CustomTooltip from "@components/tooltip/CustomTooltip";
import Image from "@components/item/Image";
import { Flex } from "antd";
import BankTransferButton from "../../../components/button/BankTransferButton";
import {
  calculateProductPrice,
  calculateShippingThreshold,
} from "@utils/price";
import { useTranslation } from "react-i18next";
import { toPrice } from "@utils/functions";
import useOrderIdStore from "@store/order/useOrderIdStore";

const OrderMain = ({ setStep }: any) => {
  const { t } = useTranslation();
  const lang = window.localStorage.getItem("language");
  const [addressError, setAddressError] = useState<string | null>(null);
  // API 관련 설정
  const { postPayment, getDeliveryList, getMemberSubscription, deleteCart } =
    useApiRequest();

  const { data: subscribeDetail } = useQuery({
    queryKey: ["subscribeDetail"],
    queryFn: () => getMemberSubscription(),
  });

  const { data: deliveryList } = useQuery({
    queryKey: ["deliveryList"],
    queryFn: () => getDeliveryList(),
  });

  const { mutate: deleteMutate } = useMutation({
    mutationFn: deleteCart,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["cartList"] }),
  });

  // 기본 설정
  const theme = useTheme();
  const { userInfo } = useUserInfoStore();

  const navigate = useNavigate();
  const { getOrderItems, orderType, cartIds } = useOrderStore();
  const orderItems =
    getOrderItems<
      typeof orderType extends "PURCHASE" ? PurchaseData : CartData
    >();

  const language = localStorage.getItem("language");
  // 상태 관리
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<"card" | "transfer">(
    "card",
  );
  const hasAddress = !!deliveryList;

  const isKoreanShipping = deliveryList?.shipping_country === "KR";
  const { setOrderId } = useOrderIdStore();

  // 배송지 수정 페이지로 이동
  const handleAddressEdit = () => {
    setStep(1);
  };

  // 결제 데이터 생성을 위한 함수
  const createPaymentData = () => {
    const paymentData = {
      seller_list: orderItems.map((seller: any) => {
        const groupedProducts = seller.product_list.reduce(
          (acc: any, product: any) => {
            if (!acc[product.product_id]) {
              acc[product.product_id] = {
                product_id: product.product_id,
                option_list: [],
              };
            }

            acc[product.product_id].option_list = [
              ...acc[product.product_id].option_list,
              {
                color_id: product.color_id,
                size_id: product.size_id,
                amount: product.amount,
              },
            ];

            return acc;
          },
          {},
        );

        return {
          seller_id: seller.seller_id,
          product_list: Object.values(groupedProducts),
        };
      }),
    };
    // console.log("결제요청데이터", paymentData);

    return paymentData;
  };

  // 결제 요청 mutation
  const { mutateAsync: createPayment } = useMutation({
    mutationFn: async () => {
      const paymentData = createPaymentData();
      return postPayment(paymentData);
    },

    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["orderList"] });
      setOrderId(res?.order_id);
    },

    onError: (error: any) => {
      if (error.response.data.data.error_code === "0006") {
        // 품절 Modal
        setIsModalOpen(true);
      }
      return null;
    },
  });

  const calculations = useMemo(() => {
    if (!orderItems)
      return {
        sellerTotals: {},
        totalShipping: 0,
        totalAmount: 0,
        totalDiscount: 0,
        totalPaymentAmount: 0,
      };

    const sellerTotals = orderItems.reduce((acc: any, seller: any) => {
      let OriginalProductTotal = 0;
      let ProductTotal = 0;
      let PaymentProductTotal = 0;
      let sellerDiscount = 0; // 셀러별 할인 금액 초기화

      seller.product_list.forEach((product: any) => {
        OriginalProductTotal += product.price * product.amount;
        ProductTotal += product.price * 2 * product.amount;

        const paymentPrice = calculateProductPrice(
          product.price,
          subscribeDetail?.is_subscribed,
          product.product_condition === "NEW",
        );

        PaymentProductTotal += paymentPrice * product.amount;

        // 할인 금액 계산 (구독자이면서 새 상품일 경우에만)
        if (
          subscribeDetail?.is_subscribed &&
          product.product_condition === "NEW"
        ) {
          sellerDiscount += (product.price * 2 - paymentPrice) * product.amount;
        }
      });

      const freeShippingThreshold = calculateShippingThreshold(
        seller.free_shipping_fee,
        subscribeDetail?.is_subscribed,
        isKoreanShipping,
      );

      const shippingFee =
        PaymentProductTotal === 0
          ? 0
          : PaymentProductTotal >= freeShippingThreshold
            ? 0
            : seller.shipping_fee;

      return {
        ...acc,
        [seller.seller_id]: {
          ProductTotal,
          OriginalProductTotal,
          PaymentProductTotal,
          shippingFee,
          sellerDiscount, // 셀러별 할인 금액 추가
        },
      };
    }, {});

    const totalShipping = Object.values(sellerTotals).reduce(
      (sum: number, { shippingFee }: any) => sum + shippingFee,
      0,
    );

    const totalAmount = Object.values(sellerTotals).reduce(
      (sum: number, { ProductTotal }: any) => sum + ProductTotal,
      0,
    );

    const totalPaymentAmount = Object.values(sellerTotals).reduce(
      (sum: number, { PaymentProductTotal }: any) => sum + PaymentProductTotal,
      0,
    );

    const totalDiscount = Object.values(sellerTotals).reduce(
      (sum: number, { sellerDiscount }: any) => sum + sellerDiscount,
      0,
    );

    return {
      sellerTotals,
      totalShipping,
      totalAmount,
      totalDiscount,
      totalPaymentAmount,
    };
  }, [orderItems, subscribeDetail?.is_subscribed, isKoreanShipping]);

  return (
    <Container>
      <AddressButton
        title={!hasAddress ? "배송지 등록" : "배송지 수정"}
        onClick={handleAddressEdit}
      />

      {hasAddress && (
        <UserInfoBox>
          <div>{deliveryList?.receiver_name}</div>(
          {deliveryList?.shipping_postcode}) {deliveryList?.shipping_address}
          {deliveryList?.shipping_country === "KR" &&
            deliveryList?.shipping_address_detail &&
            ` ${deliveryList.shipping_address_detail}`}
          <div>{deliveryList?.receiver_phone}</div>
          <UserCountryMessage>
            {!isKoreanShipping && (
              <>
                {t("해외배송은 발송 시 배송비가 별도로 계산되어 청구됩니다.")}
              </>
            )}
          </UserCountryMessage>
        </UserInfoBox>
      )}
      {addressError && <ErrorMessage>{addressError}</ErrorMessage>}

      {/* 상품 목록 */}
      {orderItems?.map((seller, index) => (
        <div key={seller.seller_id}>
          <Line />
          {/* 셀러 정보 */}
          <UserProfileContainer>
            <UserImage
              src={seller.profile_image || theme.image.default_user.default}
              alt="profile_image"
            />
            <SellerName>{seller.nickname}</SellerName>
          </UserProfileContainer>

          {/* 상품 목록 */}
          {seller.product_list?.map((item, index) => (
            <CartItemContainer key={item.cart_id}>
              <ItemContainer>
                <Flex gap={"1rem"}>
                  <ItemImageWrapper>
                    <ItemImage src={item.thumbnail_url} alt="image" />
                  </ItemImageWrapper>

                  <ItemDetails>
                    <BrandName>
                      {language === "ko"
                        ? item.brand_name_ko
                        : item.brand_name_en}
                    </BrandName>

                    <ItemName>
                      {language === "ko"
                        ? item.product_name_ko
                        : item.product_name_en}
                    </ItemName>

                    <ItemOption>
                      {t("옵션")} : {item.color_name} / {item.size_name}
                    </ItemOption>
                  </ItemDetails>
                </Flex>

                <Amount>
                  {subscribeDetail?.is_subscribed &&
                  item.product_condition === "NEW" ? (
                    <>
                      {t("[구독 멤버 할인 50%]")}{" "}
                      {toPrice(item.price * item.amount)} / {t("수량")}{" "}
                      {item.amount}
                      {language === "ko" && "개"}
                    </>
                  ) : (
                    <>
                      {toPrice(item.price * 2 * item.amount)} / {t("수량")}{" "}
                      {item.amount}
                      {language === "ko" && "개"}
                    </>
                  )}
                </Amount>
              </ItemContainer>
            </CartItemContainer>
          ))}
          {/* 셀러별 합계 정보 */}
          <SellerSummary>
            <SummaryRow>
              <SummaryLabel>
                <SummaryLabel>{t("배송비")}</SummaryLabel>

                <CustomTooltip text="쇼핑 툴팁">
                  <TooltipBox>
                    <Image
                      src={theme.image.warning_circle.default}
                      alt="image"
                      width="1.6rem"
                      height="1.6rem"
                    />
                  </TooltipBox>
                </CustomTooltip>
              </SummaryLabel>

              <SummaryValue>
                <FreeShipping isKoreanShipping={isKoreanShipping}>
                  {hasAddress && isKoreanShipping
                    ? toPrice(
                        calculations.sellerTotals[seller.seller_id]
                          ?.shippingFee,
                      )
                    : hasAddress && !isKoreanShipping
                      ? t("해외배송 별도 안내 예정")
                      : null}
                </FreeShipping>
              </SummaryValue>
            </SummaryRow>

            <SummaryRow>
              <SummaryLabel>{t("상품 금액")}</SummaryLabel>
              <SummaryValue>
                {toPrice(
                  calculations.sellerTotals[seller.seller_id]?.ProductTotal,
                )}
              </SummaryValue>
            </SummaryRow>

            <SummaryRow>
              <SummaryLabel>{t("할인 금액")}</SummaryLabel>
              <SummaryValue green>
                {calculations.sellerTotals[seller.seller_id]?.sellerDiscount > 0
                  ? `- ${toPrice(calculations.sellerTotals[seller.seller_id]?.sellerDiscount)}`
                  : toPrice(
                      calculations.sellerTotals[seller.seller_id]
                        ?.sellerDiscount,
                    )}
              </SummaryValue>
            </SummaryRow>
          </SellerSummary>
        </div>
      ))}

      <PriceDetails>
        <PriceRow>
          <SummaryLabel>{t("총 배송비")}</SummaryLabel>
          <span>{toPrice(calculations.totalShipping)}</span>
        </PriceRow>

        <PriceRow>
          <SummaryLabel>{t("총 상품 금액")}</SummaryLabel>
          <span>{toPrice(calculations.totalAmount)}</span>
        </PriceRow>

        <PriceRow>
          <SummaryLabel>{t("총 할인 금액")}</SummaryLabel>
          <SummaryValue green>
            <span>
              {calculations.totalDiscount > 0
                ? `- ${toPrice(calculations.totalDiscount)}`
                : toPrice(Math.abs(calculations.totalDiscount))}
            </span>
          </SummaryValue>
        </PriceRow>
      </PriceDetails>

      {/* 토스, 무통장 입금 분리 시 */}
      <CheckoutBox>
        <span>{t("결제 방법")}</span>
        <BtnBox>
          <BasicButton
            title="신용/체크카드"
            width="16rem"
            outlined
            height="5.2rem"
            borderColor="#E0E0E0"
            active={selectedMethod === "card"}
            onClick={() => setSelectedMethod("card")}
          />
          <BasicButton
            title="무통장입금"
            width="16rem"
            outlined
            borderColor="#E0E0E0"
            height="5.2rem"
            active={selectedMethod === "transfer"}
            onClick={() => setSelectedMethod("transfer")}
          />
        </BtnBox>
      </CheckoutBox>

      <CheckoutBtnBox>
        {selectedMethod === "card" ? (
          <TossPaymentButton
            setAddressError={setAddressError}
            title=""
            totalPrice={
              calculations?.totalPaymentAmount + calculations.totalShipping
            }
            buyerName={userInfo?.name || ""}
            productName={
              orderItems?.length === 1
                ? `${orderItems[0]?.product_list[0]?.product_name_ko}`
                : `${orderItems[0]?.product_list[0]?.product_name_ko} 외 ${
                    orderItems.reduce(
                      (total, seller) => total + seller.product_list.length,
                      0,
                    ) - 1
                  }개`
            }
            paymentData={createPaymentData()}
            onPaymentStart={createPayment}>
            <TotalTitle>
              {lang === "en" && <span> {t("결제")} ₩</span>}
              <TotalPrice>
                <span>
                  {(
                    calculations.totalPaymentAmount + calculations.totalShipping
                  ).toLocaleString()}
                </span>
              </TotalPrice>{" "}
              {/* {lang === "en" && <span>{t("원")}</span>}{" "} */}
              {lang === "ko" && <span> 원 결제하기</span>}
            </TotalTitle>
          </TossPaymentButton>
        ) : (
          <BankTransferButton
            paymentData={createPaymentData()}
            onPaymentStart={createPayment}>
            <TotalTitle>
              {lang === "en" && <span> {t("결제")} ₩</span>}
              <TotalPrice>
                <span>
                  {(
                    calculations?.totalPaymentAmount +
                    calculations?.totalShipping
                  )?.toLocaleString()}
                </span>{" "}
              </TotalPrice>{" "}
              {lang === "en" && <span>{t("원")}</span>}{" "}
              {lang === "ko" && <span> 원 결제하기</span>}
            </TotalTitle>
          </BankTransferButton>
        )}
      </CheckoutBtnBox>

      <BasicModal
        open={isModalOpen}
        setOpen={setIsModalOpen}
        activeEvent={() => navigate("/cart")}
        activeName="확인"
        notice>
        <ModalContent
          mainMessage="상품이 픔절되어 주문이 불가 합니다."
          subMessage="확인 후 다시 주문해주세요 😭"
        />
      </BasicModal>
    </Container>
  );
};
export default OrderMain;

const Container = styled.div(() => ({
  padding: "2rem",
}));

const UserInfoBox = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  fontSize: "1.4rem",
  margin: "2rem 0 0 0",
}));

const UserCountryMessage = styled.div(({ theme }) => ({
  fontSize: "1.2rem",
  color: theme.color.orange,
  marginBottom: "1rem",
}));

const UserProfileContainer = styled.div(() => ({
  display: "flex",
  alignItems: "center",
  gap: "1rem",
  borderTop: "4px solid ",
  padding: "1rem 0 0 2rem",
}));

const UserImage = styled.img(({}) => ({
  width: "4rem",
  height: "4rem",
  borderRadius: "50%",
  objectFit: "cover",
}));

const SellerName = styled.div(() => ({
  fontSize: "1.4rem",
}));

const PriceDetails = styled.div(({ theme }) => ({
  padding: "2rem 1rem ",
  gap: "2rem",
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.color.light_green,
  margin: "2rem 0",
}));

const PriceRow = styled.div(() => ({
  display: "flex",
  justifyContent: "space-between",
  fontSize: "1.6rem",
}));

const CheckoutBtnBox = styled.div(() => ({
  display: "flex",
  justifyContent: "center",
  marginTop: "6.5rem",
}));

const TotalPrice = styled.div(({ theme }) => ({
  color: theme.color.mint,
  fontSize: "1.6rem",
  fontWeight: theme.fontWeight.bold,
}));
const CheckoutBox = styled.div(({ theme }) => ({
  display: "flex",
  gap: "2rem",
  fontSize: "1.6rem",
  flexDirection: "column",
}));

const BtnBox = styled.div(() => ({
  display: "flex",
  gap: "2rem",
  alignItems: "center",
  justifyContent: "center",
}));

const TotalTitle = styled.div(() => ({
  fontSize: "1.6rem",
  display: "flex",
}));

const CartItemContainer = styled.div(({ theme }) => ({
  display: "flex",
  padding: "2rem 0 1.6rem 0",
  borderBottom: `1px solid ${theme.color.border_gray}`,
}));

const ItemContainer = styled.div({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

const ItemImageWrapper = styled.div({
  width: "6rem",
  height: "6rem",
});

const ItemImage = styled.img({
  width: "6rem",
  height: "6rem",
  objectFit: "cover",
});

const ItemDetails = styled.div({
  height: "6rem",
  gap: "0.8rem",
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

const BrandName = styled.div(({ theme }) => ({
  fontSize: "1.4rem",
  fontWeight: theme.fontWeight.bold,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",

  ">div": {
    fontWeight: theme.fontWeight.regular,
    fontSize: "1.2rem",
  },
}));

const ItemName = styled.div({
  fontSize: "1.2rem",
});

const ItemOption = styled.div(({ theme }) => ({
  fontSize: "1.2rem",
  marginBottom: "1rem",
  color: theme.color.gray,
}));

export const TitleGray = styled.div(({ theme }) => ({
  color: theme.color.gray,
  fontSize: "1.6rem",
}));

const SellerSummary = styled.div(({ theme }) => ({
  padding: "2rem 0",
  gap: "2rem",
  display: "flex",
  flexDirection: "column",
  borderBottom: `1px solid ${theme.color.black}`,

  "&:last-child": {
    borderBottom: "none",
  },
}));

const SummaryRow = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const SummaryLabel = styled.div(({ theme }) => ({
  color: theme.color.gray,
  fontSize: "1.6rem",
  display: "flex",
  alignItems: "center",
  gap: "1rem",
  whiteSpace: "nowrap",
}));

const SummaryValue = styled.div<{ green?: boolean }>(({ theme, green }) => ({
  fontSize: "1.6rem",
  display: "flex",
  alignItems: "center",
  gap: "1rem",
  color: green ? theme.color.green : theme.color.black,
}));

const FreeShipping = styled.span<{ isKoreanShipping: boolean }>(
  ({ theme, isKoreanShipping }) => ({
    fontSize: "1.6rem",
    color: isKoreanShipping ? theme.color.black : theme.color.orange,
    textAlign: "end",
  }),
);

const TooltipBox = styled.div(({ theme }) => ({
  cursor: "pointer",
  marginTop: "0.2rem",
}));

const Line = styled.span(({ theme }) => ({
  width: "100%",
  height: "1px",
  borderBottom: `1px solid ${theme.color.border_gray}`,

  "&:last-child": {
    borderBottom: "none",
  },
}));

const Amount = styled.div(({ theme }) => ({
  color: theme.color.green,
  fontSize: "1.2rem",
  fontWeight: theme.fontWeight.medium,
  marginTop: "1.6rem",
}));

const ErrorMessage = styled.div(({ theme }) => ({
  color: theme.color.orange,
  fontSize: "1.2rem",
  height: "1.5rem",
  margin: "0.8rem 0",
}));
